import { useState } from "react";
import Button from "../../design-system/primitives/components/Button/Button";
import Toast from "../../design-system/primitives/components/Toast/Toast";
import type {
  FeedbackAnswers,
  FeedbackFormDto,
  FeedbackFormField,
} from "../../api/feedback";
import type { FeedbackFieldValues } from "./FeedbackFormFields";
import {
  FeedbackFormFields,
  isFormValid,
  validateField,
} from "./FeedbackFormFields";

/**
 * A `number` field is edited as text so the length rules stay applicable; it
 * goes back to the API as a number. A value that isn't one (the input allows an
 * empty or partial entry) is sent as the string it is and lands in the answer
 * text unchanged, rather than as `NaN`.
 */
const toAnswerValue = (
  field: FeedbackFormField,
  value: string,
): string | number => {
  if (field.type !== "number") return value;
  const parsed = Number(value);
  return value !== "" && !Number.isNaN(parsed) ? parsed : value;
};

type Props = {
  form: Extract<FeedbackFormDto, { mode: "form" }>;
  isSubmitting: boolean;
  onDismiss: () => void;
  onSubmit: (answers: FeedbackAnswers) => void;
};

/**
 * A feedback form in `form` mode as a toast — a `link` form is rendered by
 * `FeedbackLinkToast` instead. Any close — the X, a swipe, Escape — is a
 * dismissal, which the API records so the form never comes back for this user.
 *
 * TODO: move the form handling onto `design-system/form` (TanStack + zod,
 * `useSchemaForm`), which would delete `validateField`/`isFormValid` and the
 * `LooseRule` narrowing in `FeedbackFormFields` — the bridge to the untyped
 * `lib/rules.jsx`. Also a UX gain: `onDynamic` holds errors back until the first
 * submit attempt, so people are told what is missing instead of facing a
 * silently disabled button. Three things stand in the way, roughly a day's work
 * and best split into a design-system PR and a small one here:
 *
 * 1. The fields come from Sanity at runtime, so the schema has to be built from
 *    `form.fields` rather than written out. Build it as
 *    `z.record(z.string(), z.string()).superRefine(...)`, issuing one issue per
 *    field path: its input type is exactly the `Record<string, string>` that
 *    `useSchemaForm`'s `schema: z.ZodType<unknown, TValues>` wants, whereas a
 *    `z.object` assembled in a loop may not satisfy that. Worth prototyping
 *    first — the rest is mechanical.
 * 2. Only `TextInputField` is bound so far. Every primitive these fields need
 *    now exists — `TextInput` (with `inputMode` for `number`), `Textarea` and
 *    `Select` — so what is left is a `SelectField` and a `TextareaField`
 *    mirroring `TextInputField`: same `Requiredness` union, same first-error
 *    `ErrorAlert`. `Select` has no `onBlur` passthrough, but `onDynamic` does
 *    not validate on blur, so `field.handleBlur` can simply be dropped there.
 * 3. `Form` renders a `<form>` around its children, but the submit button lives
 *    in the Toast's `actions` slot, outside it. `Button` has no `form`/`id`
 *    passthrough, so either add one or let the form wrap header, body and
 *    actions together.
 *
 * One thing to check visually while doing it, unchanged by the move to the
 * primitives: `ErrorAlert` is a full-width bordered box, which may be too heavy
 * per field in a compact toast.
 */
export const FeedbackToast = ({
  form,
  isSubmitting,
  onDismiss,
  onSubmit,
}: Props) => {
  const [values, setValues] = useState<FeedbackFieldValues>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    const field = form.fields.find((f) => f.key === key);
    const error = field ? validateField(field, value) : null;
    setErrors((prev) => {
      const next = { ...prev };
      if (error) next[key] = error;
      else delete next[key];
      return next;
    });
  };

  const handleSubmit = () => {
    const answers: FeedbackAnswers = {};
    for (const field of form.fields) {
      const value = values[field.key] ?? "";
      if (value === "") continue;
      answers[field.key] = toAnswerValue(field, value);
    }
    onSubmit(answers);
  };

  const canSubmit = !isSubmitting && isFormValid(form.fields, values);

  return (
    <Toast
      open
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
      icon="message-square-share"
      title={form.title}
      description={form.message ?? undefined}
      closeLabel="Schließen"
      // Feedback is never dismissed by a timer — the user decides.
      duration={Infinity}
      actions={
        <Button
          title={isSubmitting ? "Wird gesendet…" : "Senden"}
          color="accent"
          size="small"
          disabled={!canSubmit}
          onClick={handleSubmit}
        />
      }
    >
      <FeedbackFormFields
        fields={form.fields}
        values={values}
        errors={errors}
        onChange={handleChange}
      />
    </Toast>
  );
};
